import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/apiClient";
import { Mark, Wordmark, LogoImage } from "@/components/Brand";
import { ArrowRight, Mail } from "lucide-react";

const inputCls =
  "w-full rounded-md border bg-brand-panel px-3.5 py-2.5 text-sm text-brand-ink outline-none transition-all duration-150 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { exists, token }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Ese correo no tiene buena pinta."); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setResult(res.data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetLink = result?.token ? `/restablecer?token=${result.token}` : null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-brand-canvas px-6 py-12">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-8 block">
          <LogoImage size="lg" />
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-brand-ink" style={{ letterSpacing: "-0.02em" }}>Recupera tu acceso</h1>
        <p className="mt-2 text-sm text-brand-muted">Dinos tu correo y te damos un enlace para poner una contraseña nueva.</p>

        {!result && (
          <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-brand-ink">Correo</label>
              <input id="email" type="email" data-testid="forgot-email-input" value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} placeholder="tucorreo@ejemplo.com" className={`${inputCls} ${error ? "border-brand-neg" : "border-brand-line"}`} />
              {error && <p className="mt-1.5 text-xs font-medium text-brand-neg" data-testid="forgot-error">{error}</p>}
            </div>
            <button type="submit" disabled={submitting} data-testid="forgot-submit-button" className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110 disabled:opacity-60">
              {submitting ? "Enviando…" : "Enviar enlace"} {!submitting && <ArrowRight size={16} />}
            </button>
          </form>
        )}

        {result && result.sent && (
          <div className="mt-8 flex items-start gap-3 rounded-md border border-brand-accent/30 bg-brand-accent/[0.06] p-4" data-testid="forgot-sent">
            <Mail size={18} className="mt-0.5 shrink-0 text-brand-accent" />
            <p className="text-sm leading-relaxed text-brand-ink">
              Si hay una cuenta con <b>{email.trim().toLowerCase()}</b>, te acabamos de enviar un enlace para poner una contraseña nueva. Caduca en 1 hora.
              <span className="mt-2 block text-brand-muted">¿No lo ves? Revisa la carpeta de spam o promociones.</span>
            </p>
          </div>
        )}

        {result && !result.sent && !result.token && (
          <div className="mt-8 rounded-md border border-brand-line bg-brand-panel p-4 text-sm text-brand-muted" data-testid="forgot-noaccount">
            No encontramos ninguna cuenta con ese correo. Revisa que esté bien escrito o <Link to="/registro" className="font-semibold text-brand-accent hover:underline">crea una cuenta</Link>.
          </div>
        )}

        {result && result.token && (
          <div className="mt-8 space-y-4" data-testid="forgot-token-result">
            <div className="flex items-start gap-3 rounded-md border border-brand-accent/30 bg-brand-accent/[0.06] p-4">
              <Mail size={18} className="mt-0.5 shrink-0 text-brand-accent" />
              <p className="text-sm leading-relaxed text-brand-ink">
                En la versión real este enlace llega a tu correo. Como aún no está conectado el envío, te lo mostramos aquí para que puedas probarlo.
              </p>
            </div>
            <Link to={resetLink} data-testid="forgot-continue-link" className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110">
              Poner una contraseña nueva <ArrowRight size={16} />
            </Link>
          </div>
        )}

        <p className="mt-8 text-center text-sm text-brand-muted">
          <Link to="/acceso" className="font-semibold text-brand-accent hover:underline">← Volver al acceso</Link>
        </p>
      </div>
    </div>
  );
}
